import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
    TableRow, TableCell, TableHead, TableBody, Table, 
    Box, Collapse, TableContainer 
} from '@mui/material';
import { TableVirtuoso } from 'react-virtuoso';
import { colors, spacing } from 'common/theme';
import { TextHot } from 'components/common/TextHot';
import CouponTableRow from './CouponTableRow';
import { useGetUserCouponsForBenefitMutation } from 'redux/api/commerce.api';
import { transformPaidCoupons, transformFreeCoupon, getCouponsFromBenefitResponse } from '../../utils/functions';
import { COUPON_STATUS, FILTER_BENEFITS_TYPES } from '../../utils/enums';
import Loader from 'components/common/loader';
import { generateTestId } from 'app/functions';
import { useTranslations } from 'common/hooks/useTranslations';

const HEADER_CELL_STYLE = { 
    border: 'none', 
    backgroundColor: colors.palette.white, 
    zIndex: 10,
    paddingY: `${spacing.md}px`,
    verticalAlign: 'bottom',
};

const SCROLLBAR_STYLE = {
    '&::-webkit-scrollbar': { width: '6px' },
    '&::-webkit-scrollbar-track': { marginTop: '40px', backgroundColor: 'transparent' },
    '&::-webkit-scrollbar-thumb': { backgroundColor: colors.system.border.default, borderRadius: '10px' }
};

const VirtuosoTableComponents = {
    Scroller: React.forwardRef((props, ref) => (
        <TableContainer component={Box} {...props} ref={ref} sx={SCROLLBAR_STYLE} />
    )),
    Table: (props) => (
        <Table {...props} size="small" stickyHeader sx={{ borderCollapse: 'separate', minWidth: '800px' }} />
    ),
    TableHead: TableHead,
    TableRow: ({ item, ...props }) => <TableRow {...props} />,
    TableBody: TableBody,
};

function CouponTable({ benefit, testID, actionsBaseTestID: externalActionsBaseTestID, isExpanded, onArchive, onCancel, activeCancelCouponId, isPaidCoupons = true, activeFilters, onCouponStatusUpdate, onCouponArchiveUpdate }) {
    const [coupons, setCoupons] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [listHeight, setListHeight] = useState(0);
    const [getCouponsForBenefit, { isLoading }] = useGetUserCouponsForBenefitMutation();
    
    const isFetchingRef = useRef(false);
    const lastFetchedPageRef = useRef(-1);
    const justRemovedCouponRef = useRef(false);
    const { translate } = useTranslations();

    useEffect(() => {
        if (onCouponStatusUpdate && benefit.id) {
            const updateStatus = (benefitId, couponCode, newStatus, filters) => {
                if (benefitId === benefit.id) {
                    setCoupons(prev => {
                        const selectedFilter = filters?.selectedFilter || FILTER_BENEFITS_TYPES.ACTIVE;
                        if (newStatus === COUPON_STATUS.REFUND && selectedFilter === FILTER_BENEFITS_TYPES.ACTIVE) {
                            justRemovedCouponRef.current = true;
                            setTimeout(() => { justRemovedCouponRef.current = false; }, 500);
                            return prev.filter(c => String(c.code || c.id) !== String(couponCode));
                        }
                        return prev.map(c => String(c.code || c.id) === String(couponCode) ? { ...c, status: newStatus } : c);
                    });
                }
            };
            onCouponStatusUpdate(benefit.id, updateStatus);
            return () => onCouponStatusUpdate(benefit.id, null);
        }
    }, [onCouponStatusUpdate, benefit.id]);

    useEffect(() => {
        if (onCouponArchiveUpdate && benefit.id && isPaidCoupons) {
            const updateArchive = (benefitId, couponCode, isArchived) => {
                if (benefitId !== benefit.id) return;
                const selectedFilter = activeFilters?.selectedFilter || FILTER_BENEFITS_TYPES.ACTIVE;
                const shouldRemove = (selectedFilter === FILTER_BENEFITS_TYPES.ACTIVE && isArchived) ||
                    (selectedFilter === FILTER_BENEFITS_TYPES.ARCHIVED && !isArchived);
                if (shouldRemove) {
                    justRemovedCouponRef.current = true;
                    setTimeout(() => { justRemovedCouponRef.current = false; }, 500);
                    setCoupons(prev => prev.filter(c => String(c.code || c.id) !== String(couponCode)));
                } else {
                    setCoupons(prev => prev.map(c => String(c.code || c.id) === String(couponCode) ? { ...c, is_archived: isArchived } : c));
                }
            };
            onCouponArchiveUpdate(benefit.id, updateArchive);
            return () => onCouponArchiveUpdate(benefit.id, null);
        }
    }, [onCouponArchiveUpdate, benefit.id, isPaidCoupons, activeFilters?.selectedFilter]);

    useEffect(() => {
        if (justRemovedCouponRef.current) return;
        setCoupons([]);
        setPage(1);
        setHasMore(true);
        setListHeight(0);
        isFetchingRef.current = false;
        lastFetchedPageRef.current = -1;
    }, [activeFilters, benefit.id, isPaidCoupons]);

    useEffect(() => {
        if (!isPaidCoupons && isExpanded) {
            let allItems = [];
            benefit?.orders?.forEach(order => {
                order.order_items?.forEach(item => {
                    allItems.push(transformFreeCoupon(item, order.order_id, order.order_date));
                });
            });
            setCoupons(allItems);
            setHasMore(false);
        }
    }, [isExpanded, isPaidCoupons, benefit]);

    const fetchData = useCallback(async (pageToFetch) => {
        if (!isPaidCoupons || isLoading || !hasMore || !isExpanded || isFetchingRef.current || lastFetchedPageRef.current === pageToFetch) return;

        isFetchingRef.current = true;
        lastFetchedPageRef.current = pageToFetch;

        const filter = activeFilters?.selectedFilter || FILTER_BENEFITS_TYPES.ACTIVE;
        
        try {
            const response = await getCouponsForBenefit({ 
                benefitId: benefit.id, 
                page: pageToFetch, 
                pageNum: pageToFetch, 
                filterBy: filter
            }).unwrap();

            const rawCoupons = getCouponsFromBenefitResponse(response);
            const newCoupons = transformPaidCoupons(benefit, rawCoupons);
            setHasMore(newCoupons.length > 0 && rawCoupons.length >= 5);
            setCoupons(prev => [...prev, ...newCoupons]);
            setPage(prev => prev + 1);
        } catch (error) {
            setHasMore(false);
        } finally {
            isFetchingRef.current = false;
        }
    }, [benefit, hasMore, isLoading, isExpanded, activeFilters, getCouponsForBenefit, isPaidCoupons]);

    useEffect(() => {
        if (isPaidCoupons && isExpanded && page === 1 && !isLoading && lastFetchedPageRef.current !== 1) {
            fetchData(1);
        }
    }, [isExpanded, isPaidCoupons, fetchData, page, isLoading]);

    const loadMore = useCallback(() => {
        if (page > 1 && coupons.length > 0) {
            fetchData(page);
        }
    }, [fetchData, page, coupons.length]);

    const containerHeight = coupons.length > 0 ? Math.min(listHeight + 2, 250) : 100;

    const FixedHeaderContent = () => (
        <TableRow testId={generateTestId(testID, 'header-row')}>
            <TableCell sx={{ ...HEADER_CELL_STYLE, px: 0 }} />
            <TableCell sx={HEADER_CELL_STYLE}>
                <TextHot size="l" color="subtle">{translate('my_coupons_coupon_code')}</TextHot>
            </TableCell>
            <TableCell sx={HEADER_CELL_STYLE}>
                <TextHot size="l" color="subtle">{translate('my_orders_screen_order_status_label')}</TextHot>
            </TableCell>
            <TableCell align="left" sx={HEADER_CELL_STYLE}>
                <TextHot size="l" color="subtle">{translate('my_coupons_coupons_table_header_validity')}</TextHot>
            </TableCell>
            {isPaidCoupons && (
                <TableCell align="left" sx={HEADER_CELL_STYLE}>
                    <TextHot size="l" color="subtle">{translate('my_coupons_coupons_table_header_coupon_value')}</TextHot>
                </TableCell>
            )}
            <TableCell align="left" sx={HEADER_CELL_STYLE}>
                <TextHot size="l" color="subtle">{translate('my_coupons_coupons_table_header_purchase_date')}</TextHot>
            </TableCell>
            <TableCell align="left" sx={HEADER_CELL_STYLE}>
                <TextHot size="l" color="subtle">{translate('my_orders_screen_order_number_label')}</TextHot>
            </TableCell>
        </TableRow>
    );

    return (
        <TableRow testId={generateTestId(testID, 'coupon-table-row')} sx={{ backgroundColor: 'transparent' }}>
            <TableCell 
                sx={{ 
                    padding: 0, border: 'none', 
                    backgroundColor: colors.palette.white,
                    borderBottomLeftRadius: isExpanded ? '16px' : 0,
                    borderBottomRightRadius: isExpanded ? '16px' : 0,
                }} 
                colSpan={7}
            >
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                    <Box sx={{ paddingX: `${spacing.xl}px`, width: '100%' }}>
                        <TableVirtuoso
                            style={{ height: containerHeight }} 
                            data={coupons}
                            components={VirtuosoTableComponents}
                            fixedHeaderContent={FixedHeaderContent}
                            endReached={loadMore}
                            increaseViewportBy={0} 
                            totalListHeightChanged={setListHeight}
                            itemContent={(index, coupon) => (
                                <CouponTableRow 
                                    benefit={benefit}
                                    coupon={coupon} 
                                    testID={generateTestId(testID, `coupon-row-${index}`)}
                                    actionsBaseTestID={externalActionsBaseTestID}
                                    benefitId={benefit.id}
                                    onArchive={onArchive} 
                                    onCancel={onCancel} 
                                    activeCancelCouponId={activeCancelCouponId} 
                                    isPaidCoupons={isPaidCoupons} 
                                    isMultipassCoupon={benefit.isMultipass}
                                />
                            )}
                        />
                        {isLoading && isPaidCoupons && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <Loader />
                            </Box>
                        )}
                    </Box>
                </Collapse>
            </TableCell>
        </TableRow>
    );
}

export default CouponTable;